import { createFileRoute, Link } from '@tanstack/react-router'
import * as React from 'react'

import { LOOKS } from '../lib/look-catalog'

export const Route = createFileRoute('/looks')({
  component: LooksPage,
})

function LooksPage() {
  const [activeCategory, setActiveCategory] = React.useState('All looks')
  const categories = ['All looks', 'Soft glow', 'Berry stain', 'Bare skin', 'Creator edits']
  const filteredLooks =
    activeCategory === 'All looks' || activeCategory === 'Creator edits'
      ? LOOKS
      : LOOKS.filter((look) => {
          const haystack = `${look.title} ${look.description}`.toLowerCase()
          return activeCategory === 'Soft glow'
            ? haystack.includes('glow') || haystack.includes('luminous')
            : activeCategory === 'Berry stain'
              ? haystack.includes('berry')
              : haystack.includes('bare') || haystack.includes('sheer')
        })

  return (
    <main className="ootopia-pwa-page ootopia-pwa-looks">
      <section className="ootopia-pwa-looks__intro" aria-labelledby="looks-title">
        <div className="ootopia-pwa-looks__intro-copy">
          <div className="ootopia-pwa-looks__identity">
            <div>
              <p className="ootopia-pwa-kicker">The Ootopia edit</p>
              <small>Looks worth making your own</small>
            </div>
          </div>
          <h1 id="looks-title" className="ootopia-pwa-title">
            The looks <em>worth making yours.</em>
          </h1>
          <p className="ootopia-pwa-lede">
            Browse the finish, see the formula, and remix it around your skin context and your kit.
          </p>
        </div>
        <div className="ootopia-pwa-looks__intro-aside">
          <div className="ootopia-pwa-looks__intro-collage">
            <Link
              to="/$creator/$slug"
              params={{
                creator: LOOKS[2]?.creator ?? 'ash-k-holm',
                slug: LOOKS[2]?.slug ?? 'berry-kissed',
              }}
              className="ootopia-pwa-looks__intro-media"
              aria-label="Open Berry Kissed"
            >
              <img
                src="/look-assets/berry-kissed.png"
                alt="Berry Kissed sheer berry stain look"
                loading="eager"
                decoding="async"
              />
            </Link>
            <Link
              to="/capture"
              search={{ planId: undefined, returnTo: undefined }}
              className="ootopia-pwa-looks__intro-portrait"
              aria-label="Start a skin scan"
            >
              <img
                src="/homepage/synthetic-beauty-portrait.png"
                alt="Synthetic portrait used as a skin context preview"
                loading="eager"
                decoding="async"
              />
              <span>
                <small>Skin context</small>
                <strong>Your remix starts here</strong>
              </span>
            </Link>
          </div>
          <Link
            to="/import"
            className="ootopia-pwa-looks__search"
            aria-label="Import a creator look"
          >
            <span className="ootopia-pwa-looks__search-icon" aria-hidden="true" />
            <span>
              <strong>Have a creator link?</strong>
              <small>Bring it into your shelf</small>
            </span>
            <span className="ootopia-pwa-looks__search-arrow" aria-hidden="true">
              ↗
            </span>
          </Link>
        </div>
      </section>

      <section className="ootopia-pwa-looks__featured" aria-labelledby="featured-look-title">
        <div className="ootopia-pwa-looks__featured-content">
          <p className="ootopia-pwa-kicker">The weekend edit</p>
          <h2 id="featured-look-title">Soft-focus skin, made personal.</h2>
          <p>A warm rose flush and a blurred finish that shifts with your skin context.</p>
          <Link
            to="/$creator/$slug"
            params={{
              creator: LOOKS[0]?.creator ?? 'maya-chen',
              slug: LOOKS[0]?.slug ?? 'soft-focus-glow',
            }}
            className="ootopia-pwa-action"
          >
            Try this look
          </Link>
        </div>
        <Link
          to="/$creator/$slug"
          params={{
            creator: LOOKS[0]?.creator ?? 'maya-chen',
            slug: LOOKS[0]?.slug ?? 'soft-focus-glow',
          }}
          className="ootopia-pwa-looks__featured-media"
          aria-label="Open Soft Focus Glow"
        >
          <img
            src="/homepage/synthetic-beauty-portrait.png"
            alt="Synthetic beauty portrait wearing the Soft Focus Glow look"
            loading="eager"
            decoding="async"
          />
        </Link>
      </section>

      <section className="ootopia-pwa-stack" aria-labelledby="recommended-heading">
        <div className="ootopia-pwa-looks__section-heading">
          <div>
            <p className="ootopia-pwa-kicker">The lookbook</p>
            <h2 id="recommended-heading">Find your finish</h2>
          </div>
          <div className="ootopia-pwa-looks__section-action">
            <p>Three moods. One made-for-you version.</p>
            <Link
              to="/capture"
              search={{ planId: undefined, returnTo: undefined }}
              className="ootopia-pwa-action ootopia-pwa-action--secondary"
            >
              Build from my skin
            </Link>
          </div>
        </div>

        <fieldset className="ootopia-pwa-chip-row">
          <legend className="sr-only">Look categories</legend>
          {categories.map((category) => (
            <button
              type="button"
              key={category}
              className={`ootopia-pwa-chip ${activeCategory === category ? 'is-active' : ''}`}
              aria-pressed={activeCategory === category}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </fieldset>

        {filteredLooks.length === 0 ? (
          <div className="ootopia-pwa-surface ootopia-pwa-empty" role="status">
            <h3>Nothing in this edit yet.</h3>
            <p>Try another finish or bring in a creator link to start your own shelf.</p>
            <Link to="/import" className="ootopia-pwa-action ootopia-pwa-action--accent">
              Add a creator look
            </Link>
          </div>
        ) : (
          <div className="ootopia-pwa-look-grid">
            {filteredLooks.map((look) => (
              <article key={look.slug} className="ootopia-pwa-look-card">
                <Link
                  to="/$creator/$slug"
                  params={{ creator: look.creator, slug: look.slug }}
                  className="ootopia-pwa-look-card__media"
                  aria-label={`View ${look.title}`}
                >
                  <img
                    src={look.asset}
                    alt={`${look.title} creator look`}
                    loading="eager"
                    decoding="async"
                  />
                </Link>
                <div className="ootopia-pwa-look-card__body">
                  <div className="ootopia-pwa-look-card__meta">
                    <p>{look.label}</p>
                    <span>By {look.creatorName}</span>
                  </div>
                  <h3>{look.title}</h3>
                  <p className="ootopia-pwa-look-card__description">{look.description}</p>
                  <Link
                    to="/$creator/$slug"
                    params={{ creator: look.creator, slug: look.slug }}
                    className="ootopia-pwa-look-card__link"
                  >
                    See the look <span aria-hidden="true">↗</span>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  )
}
